import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  FormControl,
  FormHelperText,
  FormLabel,
  Image,
  Input,
  Select,
  Stack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { setSite } from '../../../features/site/siteSlice';
import {
  getFrontendURL,
  getSubRoutePath,
  getBackendBaseURL,
} from '../../../utils/utils';
import ImageUploader from '../../common/image-uploader';
import {
  getTemplates,
  addSite,
  updateSite,
  getMerchants,
} from '../../../api/siteApi';

function BasicDetails({ isEdit, nextClicked }) {
  const { user } = useSelector(state => state.user);
  const { site } = useSelector(state => state.site);
  const dispatch = useDispatch();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [templates, setTemplates] = useState([]);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [templateId, setTemplate] = useState('');
  const [logo, setLogo] = useState('');

  const [selectedOwner, setSelectedOwner] = useState({});
  const [owners, setOwner] = useState([]);
  const [isMerchant, setIsMerchant] = useState(false);

  const [inProgress, setInProgress] = useState(false);
  const [displayNext, setDisplayNext] = useState(isEdit);
  const [actionStatus, setActionStatus] = useState('');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();

  useEffect(() => {
    fetchTemplates();
    const subPath = getSubRoutePath(user);
    if (subPath) {
      fetchDigiCardUsers(subPath);
    } else {
      setSelectedOwner(user._id);
      setIsMerchant(true);
    }
    if (isEdit) {
      setupComponent();
    }
    // eslint-disable-next-line
  }, []);

  const setupComponent = () => {
    setTitle(site?.title ?? '');
    setUrl(site?.url ?? '');
    setTemplate(site?.templateId ?? '');
    setSelectedOwner(site.siteOwner);
    setLogo(site.logo);
  };

  const goHome = () => {
    if (window.history.length > 1) {
      window.history.back();
    }
  };

  const createURL = event => {
    let url = event.target.value.trim().toLowerCase().split(' ').join('-');
    setTitle(event.target.value.trim());
    setUrl(url);
  };

  const fetchTemplates = () => {
    getTemplates()
      .then(response => {
        if (response !== null && response.templates.length > 0) {
          let templates = [];
          response.templates.forEach(template => {
            if (template.status) {
              templates.push({
                name: template.name,
                templateId: template.templateId,
                status: template.status,
              });
            }
          });
          setTemplates(templates);
        } else {
          setTemplates([]);
        }
      })
      .catch(error => {
        console.log(error);
        setTemplates([]);
      });
  };

  const fetchDigiCardUsers = subPath => {
    getMerchants(subPath, user._id)
      .then(response => {
        if (response !== null && response.merchants.length > 0) {
          let merchants = [];
          response.merchants.forEach(merchant => {
            if (merchant.status) {
              merchants.push({
                name: merchant.displayName || merchant.name,
                id: merchant._id,
                pinCode: merchant.pinCode,
                numberOfMonths: merchant.numberOfMonths,
              });
            }
          });
          setOwner(merchants);
        } else {
          onOpen();
          setOwner([]);
        }
      })
      .catch(error => {
        console.log(error);
        setOwner([]);
      });
  };

  const handleSite = () => {
    if (title === '' || templateId === '' || logo === '') {
      toast({
        title: 'Error',
        description: 'Please set the proper details',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    setInProgress(true);
    const siteOwner = isMerchant
      ? user
      : owners.find(owner => owner.id === selectedOwner);
    if (!siteOwner && !(isEdit && selectedOwner)) {
      toast({
        title: 'Error',
        description: 'Something went wrong, Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      setInProgress(false);
      return;
    }

    let body = {
      title,
      url,
      templateId,
      siteOwner: siteOwner.id ?? siteOwner._id,
      pinCode: isEdit ? site?.pinCode : siteOwner.pinCode,
      logo,
      creator: user._id,
      numberOfMonths: siteOwner ? siteOwner.numberOfMonths : undefined,
      updateType: 'BASIC_DETAILS',
    };

    if (isEdit) {
      body.id = site._id;
      delete body.numberOfMonths;
    } else {
      delete body.updateType;
    }

    const apiCall = isEdit ? updateSite(body) : addSite(body);
    apiCall
      .then(response => {
        setInProgress(false);
        if (response.site) {
          dispatch(setSite(response.site));
          let message = isEdit
            ? 'Site Updated Successfully.'
            : 'Site Added Successfully, Please add the follow-up details.';
          toast({
            title: 'Success',
            description: message,
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          setActionStatus(`${message}`);
          setDisplayNext(true);
          // TODO: Add support to store the URL to the user object.
        } else {
          toast({
            title: 'Error',
            description: response.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(error => {
        setInProgress(false);
        toast({
          title: 'Error',
          description: 'Something went wrong, please try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const getLogoUrl = () => {
    let url = logo;
    if (url && !url.startsWith('http')) {
      url = `${getBackendBaseURL()}/${url}`;
    }
    return url;
  };

  const onAlertClose = () => {
    if (window.history.length > 1) {
      window.history.back();
    }
    onClose();
  };

  return (
    <>
      <Card bg={useColorModeValue('white', 'gray.700')}>
        <CardBody>
          <Stack spacing="4" mt="2">
            <FormControl isRequired>
              <FormLabel>Enter Site Title</FormLabel>
              <Input
                type="text"
                name="text"
                placeholder="Welcome"
                defaultValue={title}
                disabled={isEdit}
                onChange={event => createURL(event)}
              />
              <FormHelperText>
                {url
                  ? `Your site will be at: ${getFrontendURL()}/${url}`
                  : 'Unique title for the site'}
              </FormHelperText>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Select Template</FormLabel>
              <Select
                placeholder="Select template"
                value={templateId}
                onChange={event => setTemplate(event.target.value)}
              >
                {templates.map(template => {
                  return template.status ? (
                    <option
                      key={template.templateId}
                      value={template.templateId}
                    >
                      {template.name}
                    </option>
                  ) : (
                    <></>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl
              isRequired
              display={owners && owners.length > 0 ? 'block' : 'none'}
            >
              <FormLabel>Select Merchant</FormLabel>
              <Select
                placeholder="Select Merchant"
                value={selectedOwner}
                disabled={isEdit}
                onChange={event => {
                  return setSelectedOwner(event.target.value);
                }}
              >
                {owners.map(owner => {
                  return (
                    <option key={owner.id} value={owner.id}>
                      {' '}
                      {owner.displayName || owner.name}
                    </option>
                  );
                })}
              </Select>
            </FormControl>
            {logo !== '' && isEdit ? (
              <Box w="100%">
                <Stack direction="column" spacing={4} mb={4}>
                  <Box flexShrink={0}>
                    <Image
                      src={getLogoUrl()}
                      alt="Logo"
                      maxH="100px"
                      objectFit="contain"
                      borderRadius="md"
                      border="1px solid"
                      borderColor={borderColor}
                      p={2}
                    />
                  </Box>
                  <FormControl>
                    <ImageUploader
                      label={'Change Logo'}
                      OnUpload={path => setLogo(path)}
                    />
                  </FormControl>
                </Stack>
              </Box>
            ) : (
              <FormControl isRequired>
                <FormLabel>Select Logo</FormLabel>
                <ImageUploader
                  label={'Select Logo'}
                  OnUpload={path => setLogo(path)}
                />
              </FormControl>
            )}
          </Stack>
        </CardBody>
        <CardFooter>
          <ButtonGroup w={'100%'} justifyContent="end">
            <Button colorScheme="teal" size="md" onClick={goHome}>
              Go Home
            </Button>
            <Button
              colorScheme="teal"
              isLoading={inProgress}
              loadingText="Submitting..."
              disabled={inProgress || actionStatus}
              size="md"
              onClick={handleSite}
              whiteSpace="normal"
            >
              {isEdit ? 'Update' : 'Add'}
            </Button>
            <Button
              colorScheme="teal"
              size="md"
              display={displayNext ? 'inherit' : 'none'}
              onClick={nextClicked}
            >
              Next
            </Button>
          </ButtonGroup>
        </CardFooter>
      </Card>
      <AlertDialog
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
        size="lg"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {' '}
              Alert
            </AlertDialogHeader>
            <AlertDialogBody m={3}>
              <Alert status="error">
                <AlertIcon />
                You do not have any users to add site. Please add the user first
                to add the site.
              </Alert>
            </AlertDialogBody>
            <AlertDialogFooter justifyContent={'center'}>
              <Button onClick={onAlertClose}> OK </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default BasicDetails;
